import axios from 'axios';
import type { ServiceResult } from '@core/models/actionResponse';
import type { identifier } from '@core/models/base';
import type {
  CartPackageClientCreateModel,
  Cartv2PackageDisplayModel,
  CartPackageUpdateModel,
} from '@shared/models/carts/cartPackages';
import type { SubjectFieldDisplayModel } from '@shared/models/subjects/subjectFields';
import { saveableTextNodeValue } from './cartPackageNode.service';
import { JobOrderMethod } from '@shared/models/subjects/accessCode';

const getTextNodes = (cartPkg: Cartv2PackageDisplayModel) => cartPkg.cartPackageItems.map((i) => i.textNodes).flat();
const getImageNodes = (cartPkg: Cartv2PackageDisplayModel) => cartPkg.cartPackageItems.map((i) => i.imageNodes).flat();

export const createCartPackage = async (
  jobId: identifier,
  packageId: identifier,
  subjectId: identifier,
  orderMethod: JobOrderMethod,
  cartPackage: Cartv2PackageDisplayModel,
  dataFields?: SubjectFieldDisplayModel[],
): Promise<ServiceResult<Cartv2PackageDisplayModel>> => {
  const createModel: CartPackageClientCreateModel = {
    packageId,
    subjectId,
    orderMethod,
    quantity: cartPackage.quantity,
    dataFields: (dataFields || []).map((f) => ({
      subjectId,
      name: f.name,
      value: f.value,
      templateId: f.templateId,
      optionId: f.optionId,
    })),
    greenScreenBackgroundId: cartPackage.greenScreen?.id,
    textNodes: getTextNodes(cartPackage).map((t) => ({
      packageItemTextNodeId: t.packageItemTextNodeId,
      value: saveableTextNodeValue(t),
    })),
    imageNodes: getImageNodes(cartPackage).map((i) => ({
      packageItemImageNodeId: i.id,
      subjectImageId: i.subjectImageId,
      groupImageId: i.groupImageId,
      replacementImageId: i.replacementImageId,
      cropDimensions: i.cropDimensions,
      frontendCropDimensions: i.frontendCropDimensions,
      rotation: i.rotation,
    })),
  };
  return await axios.post<Cartv2PackageDisplayModel>(`/api/customer/jobs/${jobId}/cartpackages`, createModel);
};

export const patchCartPackageDetailsToServer = async (
  jobId: identifier,
  cartPackage: Cartv2PackageDisplayModel,
): Promise<void> => {
  const urlRoot = `/api/customer/jobs/${jobId}`;
  const reqs = [];
  if (cartPackage.greenScreen) {
    reqs.push(
      axios.patch(`${urlRoot}/cartpackages/${cartPackage.id}`, { greenScreenBackgroundId: cartPackage.greenScreen.id }),
    );
  }
  const textSaves = getTextNodes(cartPackage).map((node) =>
    axios.patch(`${urlRoot}/cartpackageitems/text-nodes/${node.id}`, {
      value: saveableTextNodeValue(node),
    }),
  );
  const imageSaves = getImageNodes(cartPackage).map((node) =>
    axios.patch(`${urlRoot}/cartpackageitems/image-nodes/${node.id}`, {
      cropDimensions: node.cropDimensions ? JSON.stringify(node.cropDimensions) : null,
      frontendCropDimensions: node.frontendCropDimensions,
      rotation: node.rotation,
      subjectImageId: node.subjectImageId,
      groupImageId: node.groupImageId,
    }),
  );
  reqs.push([...textSaves, ...imageSaves]);
  await Promise.all(reqs);
};

export const deleteCartPackage = async (jobId: identifier, cartPackageId: identifier) => {
  const result = await axios.delete(`/api/customer/jobs/${jobId}/cartpackages/${cartPackageId}`);
  return result;
};

export const patchCartPackage = async (
  jobId: identifier,
  cartPackageId: identifier,
  cartPackageModel: CartPackageUpdateModel,
) => {
  const result = await axios.patch(`/api/customer/jobs/${jobId}/cartpackages/${cartPackageId}`, cartPackageModel);
  return result;
};
